import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getAuthToken } from './authToken';

const auth = () => ({ Authorization: 'Bearer ' + getAuthToken() });
const C_PROD = '#2563eb';
const C_DELIV = '#93c5fd';
const KIND_COLORS: Record<string, string[]> = {
  allegro: ['#ea580c', '#f97316', '#fb923c', '#c2410c', '#fdba74', '#9a3412', '#fed7aa'],
  prestashop: ['#db2777'], aliexpress: ['#dc2626'], manual: ['#16a34a'], apilo: ['#64748b', '#94a3b8', '#475569'], inne: ['#a855f7'],
};
const pad = (n: number) => (n < 10 ? '0' : '') + n;
const iso = (d: Date) => d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate());
const pln = (v: number) => (Number(v) || 0).toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' zł';
const plnShort = (v: number) => { const x = Number(v) || 0; return x >= 10000 ? Math.round(x / 1000).toLocaleString('pl-PL') + ' tys.' : Math.round(x).toLocaleString('pl-PL'); };
const card: any = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 14, boxSizing: 'border-box' };
const hdr: any = { fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 10 };

function periods() {
  const now = new Date();
  const d30 = new Date(now); d30.setDate(now.getDate() - 29);
  const m1 = new Date(now.getFullYear(), now.getMonth(), 1);
  return { last30: { from: iso(d30), to: iso(now) }, month: { from: iso(m1), to: iso(now) } };
}

function Donut({ a, b }: { a: number; b: number }) {
  const t = a + b;
  const R = 54, C = 2 * Math.PI * R;
  const pa = t > 0 ? a / t : 0;
  return (
    <svg viewBox="0 0 140 140" style={{ width: 140, height: 140, flexShrink: 0 }} role="img" aria-label="Produkty i dostawa">
      <circle cx="70" cy="70" r={R} fill="none" stroke="#f1f5f9" strokeWidth="18" />
      {t > 0 ? <circle cx="70" cy="70" r={R} fill="none" stroke={C_DELIV} strokeWidth="18" /> : null}
      {t > 0 ? <circle cx="70" cy="70" r={R} fill="none" stroke={C_PROD} strokeWidth="18" strokeDasharray={(pa * C) + ' ' + C} transform="rotate(-90 70 70)" /> : null}
      <text x="70" y="66" textAnchor="middle" fontSize="11" fill="#6b7280">produkty</text>
      <text x="70" y="84" textAnchor="middle" fontSize="16" fontWeight="700" fill="#111827">{t > 0 ? Math.round(pa * 100) + '%' : '—'}</text>
    </svg>
  );
}

function DailyChart({ days }: { days: any[] }) {
  const n = days.length || 1;
  const W = 640, H = 180, L = 40, B = 22, T = 8;
  const max = Math.max(1, ...days.map((d) => Number(d.total) || 0));
  const bw = (W - L - 4) / n;
  const step = n > 62 ? 14 : (n > 31 ? 7 : (n > 14 ? 3 : 1));
  const ticks = [0, 0.5, 1].map((f) => f * max);
  return (
    <svg viewBox={'0 0 ' + W + ' ' + H} style={{ width: '100%', height: 'auto', display: 'block' }} role="img" aria-label="Sprzedaż dziennie">
      {ticks.map((v, i) => {
        const y = T + (H - T - B) * (1 - v / max);
        return <g key={i}><line x1={L} x2={W} y1={y} y2={y} stroke="#f1f5f9" /><text x={L - 4} y={y + 3} fontSize="9" textAnchor="end" fill="#9ca3af">{plnShort(v)}</text></g>;
      })}
      {days.map((d, i) => {
        const x = L + 2 + i * bw;
        const hp = (H - T - B) * ((Number(d.total) - Number(d.delivery)) / max);
        const hd = (H - T - B) * (Number(d.delivery) / max);
        const base = H - B;
        const w = Math.max(1, bw - (bw > 6 ? 2 : 0.5));
        return (
          <g key={d.date}>
            <title>{d.date.split('-').reverse().join('.') + ': ' + pln(d.total) + ' (' + d.orders + ' zam., dostawa ' + pln(d.delivery) + ')'}</title>
            <rect x={x} y={base - hp} width={w} height={Math.max(0, hp)} fill={C_PROD} rx={bw > 6 ? 2 : 0} />
            <rect x={x} y={base - hp - hd} width={w} height={Math.max(0, hd)} fill={C_DELIV} />
            {i % step === 0 ? <text x={x + w / 2} y={H - 6} fontSize="9" textAnchor="middle" fill="#9ca3af">{d.date.slice(8, 10) + '.' + d.date.slice(5, 7)}</text> : null}
          </g>
        );
      })}
    </svg>
  );
}

export default function OrdersStats({ API }: { API: string }) {
  const P = periods();
  const [tab, setTab] = useState<'last30' | 'month' | 'range'>('last30');
  const [range, setRange] = useState({ from: P.month.from, to: P.month.to });
  const [data, setData] = useState<any>({});
  const [err, setErr] = useState('');
  const load = (key: string, from: string, to: string) => {
    setData((p: any) => ({ ...p, [key]: { loading: true } }));
    axios.get(API + '/orders-stats', { params: { date_from: from, date_to: to }, headers: auth() })
      .then((r: any) => setData((p: any) => ({ ...p, [key]: r.data })))
      .catch((e: any) => { setErr((e.response && e.response.data && e.response.data.detail) || 'Nie udało się pobrać statystyk.'); setData((p: any) => ({ ...p, [key]: null })); });
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load('last30', P.last30.from, P.last30.to); load('month', P.month.from, P.month.to); load('range', range.from, range.to); }, [API]);
  const cur = data[tab];
  const colorOf = (() => { const used: Record<string, number> = {}; return (c: any) => { const pal = KIND_COLORS[c.kind] || KIND_COLORS.inne; const i = used[c.kind] || 0; used[c.kind] = i + 1; return pal[i % pal.length]; }; })();
  const chans: any[] = (cur && cur.channels) || [];
  const colored = chans.map((c: any) => ({ ...c, color: colorOf(c) }));
  const maxCh = Math.max(1, ...chans.map((c: any) => c.total));
  const sum = (cur && cur.summary) || null;
  const kpi = (key: string, label: string) => {
    const d = data[key];
    const active = tab === key;
    return (
      <div onClick={() => setTab(key as any)} style={{ ...card, cursor: 'pointer', borderColor: active ? '#2563eb' : '#e5e7eb', boxShadow: active ? '0 0 0 2px #bfdbfe' : 'none' }}>
        <div style={{ fontSize: 12, color: '#6b7280' }}>{label}</div>
        <div style={{ fontSize: 20, fontWeight: 700, color: '#111827', marginTop: 4 }}>{d && d.summary ? pln(d.summary.total) : (d && d.loading ? '…' : '—')}</div>
        <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{d && d.summary ? d.summary.orders.toLocaleString('pl-PL') + ' zamówień' : ''}</div>
      </div>
    );
  };
  return (
    <div style={{ fontFamily: 'system-ui, -apple-system, sans-serif', maxWidth: 1100 }}>
      <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Statystyki sprzedaży</div>
      <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 12 }}>Bez zamówień anulowanych. Kwoty w walutach obcych przeliczone na złote po bieżącym kursie NBP{cur && cur.rates_date ? ' (' + cur.rates_date + ')' : ''}.</div>
      {err ? <div style={{ fontSize: 13, color: '#b91c1c', marginBottom: 10 }}>{err}</div> : null}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 10, marginBottom: 12 }}>
        {kpi('last30', 'Ostatnie 30 dni')}
        {kpi('month', 'Ten miesiąc')}
        <div onClick={() => setTab('range')} style={{ ...card, cursor: 'pointer', borderColor: tab === 'range' ? '#2563eb' : '#e5e7eb', boxShadow: tab === 'range' ? '0 0 0 2px #bfdbfe' : 'none' }}>
          <div style={{ fontSize: 12, color: '#6b7280' }}>Wybrany zakres</div>
          <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }} onClick={(e) => e.stopPropagation()}>
            <input type="date" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} style={{ fontSize: 12, padding: '3px 6px', border: '1px solid #d1d5db', borderRadius: 6, flex: '1 1 110px' }} />
            <input type="date" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} style={{ fontSize: 12, padding: '3px 6px', border: '1px solid #d1d5db', borderRadius: 6, flex: '1 1 110px' }} />
            <button onClick={() => { setTab('range'); load('range', range.from, range.to); }} style={{ fontSize: 12, padding: '3px 10px', borderRadius: 6, border: '1px solid #2563eb', background: '#2563eb', color: '#fff', cursor: 'pointer' }}>Pokaż</button>
          </div>
          <div style={{ fontSize: 13, fontWeight: 700, marginTop: 6 }}>{data.range && data.range.summary ? pln(data.range.summary.total) + ' · ' + data.range.summary.orders.toLocaleString('pl-PL') + ' zam.' : (data.range && data.range.loading ? '…' : '')}</div>
        </div>
      </div>

      {cur && cur.loading ? <div style={{ fontSize: 13, color: '#6b7280' }}>⏳ Liczę…</div> : null}
      {sum ? (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 10, marginBottom: 10 }}>
            <div style={card}>
              <div style={hdr}>Sprzedaż razem: produkty i dostawa ({cur.date_from.split('-').reverse().join('.')} – {cur.date_to.split('-').reverse().join('.')})</div>
              <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
                <Donut a={sum.products} b={sum.delivery} />
                <div style={{ fontSize: 13, display: 'grid', gap: 6, minWidth: 160 }}>
                  <div><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: C_PROD, marginRight: 6 }} />Produkty (bez dostawy): <b>{pln(sum.products)}</b></div>
                  <div><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: C_DELIV, marginRight: 6 }} />Koszty dostawy: <b>{pln(sum.delivery)}</b></div>
                  <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 6 }}>Razem z dostawą: <b style={{ fontSize: 15 }}>{pln(sum.total)}</b></div>
                  <div style={{ color: '#6b7280' }}>{sum.orders.toLocaleString('pl-PL')} zamówień · średnio {pln(sum.orders ? sum.total / sum.orders : 0)}</div>
                </div>
              </div>
            </div>
            <div style={card}>
              <div style={hdr}>Sprzedaż wg kont i kanałów</div>
              {colored.length === 0 ? <div style={{ fontSize: 13, color: '#6b7280' }}>Brak sprzedaży w tym okresie.</div> : null}
              {colored.map((c: any) => (
                <div key={c.key} style={{ marginBottom: 9 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 12, marginBottom: 3 }}>
                    <span style={{ fontWeight: 600, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}><span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: 2, background: c.color, marginRight: 6 }} />{c.label}</span>
                    <span style={{ whiteSpace: 'nowrap' }}><b>{pln(c.total)}</b> <span style={{ color: '#9ca3af' }}>· {c.orders} zam.</span></span>
                  </div>
                  <div style={{ height: 10, background: '#f1f5f9', borderRadius: 5, overflow: 'hidden', display: 'flex' }} title={'produkty ' + pln(c.products) + ', dostawa ' + pln(c.delivery)}>
                    <div style={{ width: (100 * c.products / maxCh) + '%', background: c.color }} />
                    <div style={{ width: (100 * c.delivery / maxCh) + '%', background: c.color, opacity: 0.35 }} />
                  </div>
                </div>
              ))}
              {colored.length ? <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>Pełny kolor: produkty, jaśniejszy: dostawa.</div> : null}
            </div>
          </div>

          <div style={{ ...card, marginBottom: 10 }}>
            <div style={hdr}>Sprzedaż dziennie <span style={{ fontWeight: 400, color: '#9ca3af' }}>(najedź na słupek, aby zobaczyć kwotę)</span></div>
            <DailyChart days={cur.days || []} />
            <div style={{ display: 'flex', gap: 14, fontSize: 12, color: '#6b7280', marginTop: 6 }}>
              <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: C_PROD, marginRight: 5 }} />produkty</span>
              <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: C_DELIV, marginRight: 5 }} />dostawa</span>
            </div>
          </div>

          <div style={{ ...card, overflowX: 'auto' }}>
            <div style={hdr}>Zestawienie</div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 520 }}>
              <thead><tr style={{ color: '#6b7280', fontSize: 12, textAlign: 'right' }}>
                <th style={{ textAlign: 'left', padding: '4px 6px', fontWeight: 600 }}>Konto / kanał</th><th style={{ padding: '4px 6px', fontWeight: 600 }}>Zamówień</th>
                <th style={{ padding: '4px 6px', fontWeight: 600 }}>Produkty</th><th style={{ padding: '4px 6px', fontWeight: 600 }}>Dostawa</th><th style={{ padding: '4px 6px', fontWeight: 600 }}>Razem</th>
              </tr></thead>
              <tbody>
                {colored.map((c: any) => (
                  <tr key={c.key} style={{ borderTop: '1px solid #f1f5f9', textAlign: 'right' }}>
                    <td style={{ textAlign: 'left', padding: '5px 6px' }}><span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: 2, background: c.color, marginRight: 6 }} />{c.label}</td>
                    <td style={{ padding: '5px 6px' }}>{c.orders.toLocaleString('pl-PL')}</td>
                    <td style={{ padding: '5px 6px' }}>{pln(c.products)}</td>
                    <td style={{ padding: '5px 6px' }}>{pln(c.delivery)}</td>
                    <td style={{ padding: '5px 6px', fontWeight: 600 }}>{pln(c.total)}</td>
                  </tr>
                ))}
                <tr style={{ borderTop: '2px solid #e5e7eb', textAlign: 'right', fontWeight: 700 }}>
                  <td style={{ textAlign: 'left', padding: '6px' }}>Razem</td><td style={{ padding: '6px' }}>{sum.orders.toLocaleString('pl-PL')}</td>
                  <td style={{ padding: '6px' }}>{pln(sum.products)}</td><td style={{ padding: '6px' }}>{pln(sum.delivery)}</td><td style={{ padding: '6px' }}>{pln(sum.total)}</td>
                </tr>
              </tbody>
            </table>
            {cur.missing_rates && cur.missing_rates.length ? <div style={{ fontSize: 11, color: '#b45309', marginTop: 6 }}>Brak kursu NBP dla: {cur.missing_rates.join(', ')} — te kwoty nie są wliczone.</div> : null}
          </div>
        </>
      ) : null}
    </div>
  );
}
